import React, { useMemo } from 'react';
import { Thread as ThreadIcon } from '@bufferapp/ui/Icon';

import MessageIcon from '@bufferapp/ui/Icon/Icons/Message';
import { ClockIcon, WarningIcon } from '@bufferapp/components';
import { grayDark, red } from '@bufferapp/ui/style/colors';

import PostFooterButtons from '~/post/components/PostFooterButtons';
import { PostAction } from '~/post/types';
import CardFooter from '../CardFooter';
import {
  CardFooterContent,
  CommentIconWrapper,
  IconWrapper,
  Message,
  ThreadCounter,
  ThreadIconWrapper,
} from '../CardFooter/styles';

type DraftFooterProps = {
  isScheduled?: boolean;
  isPastDue?: boolean;
  description?: string;
  hasFirstComment?: boolean;
  actionMessage?: string;
  isPerformingAction?: boolean;
  hasEditPermission?: boolean;
  singleActions: PostAction[];
  stackedActions: PostAction[];
  onDeleteClick?: (args?: unknown) => void;
  onEditClick?: (args?: unknown) => void;
  threadCounter: number;
  shouldShowThreadsMigrationModal: boolean;
};

const DraftFooter = ({
  isScheduled,
  isPastDue,
  description,
  hasFirstComment,
  actionMessage,
  isPerformingAction,
  hasEditPermission,
  singleActions,
  stackedActions,
  onDeleteClick,
  onEditClick,
  threadCounter,
  shouldShowThreadsMigrationModal,
}: DraftFooterProps): JSX.Element => {
  const renderButtons = hasEditPermission && !actionMessage;
  const icon = useMemo(() => {
    if (isPastDue) {
      return <WarningIcon color={red} />;
    }
    if (isScheduled) {
      return <ClockIcon />;
    }
  }, [isPastDue, isScheduled]);

  return (
    <CardFooter>
      <CardFooterContent>
        {threadCounter > 0 && (
          <>
            <ThreadIconWrapper>
              <ThreadIcon
                color={grayDark}
                size="medium"
                role="img"
                title="This is a Thread post"
              />
            </ThreadIconWrapper>
            <ThreadCounter
              type="p"
              textColor={grayDark}
              data-testid="threads-counter"
            >
              {threadCounter}
            </ThreadCounter>
          </>
        )}
        {description && (
          <>
            {icon && <IconWrapper>{icon}</IconWrapper>}
            <Message type="p" textColor={isPastDue ? red : ''}>
              {description}
            </Message>
          </>
        )}
        {hasFirstComment && (
          <CommentIconWrapper title="Post includes a comment">
            <MessageIcon />
          </CommentIconWrapper>
        )}
      </CardFooterContent>

      {renderButtons && (
        <PostFooterButtons
          isLoading={isPerformingAction}
          singleActions={singleActions}
          stackedActions={stackedActions}
          onDeleteClick={onDeleteClick}
          onEditClick={onEditClick}
          shouldShowThreadsMigrationModal={shouldShowThreadsMigrationModal}
        />
      )}
      {!!actionMessage && <Message type="p">{actionMessage}</Message>}
    </CardFooter>
  );
};

DraftFooter.defaultProps = {
  isScheduled: false,
  isPastDue: false,
  description: null,
  hasFirstComment: false,
  actionMessage: null,
  isPerformingAction: false,
  hasEditPermission: false,
};

export default DraftFooter;
